import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ReportContent: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('blog.backToBlog')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Viewer Guide • August 11, 2026
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          How to Report Inappropriate Content
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          IndeeClips is built for everyone, and keeping it safe requires community participation. If you encounter content that violates our policies, reporting it helps protect other viewers and maintain platform quality. This guide explains when and how to report inappropriate videos, channels, and comments effectively.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          What Qualifies as Inappropriate Content
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Before reporting, understand what violates IndeeClips policies. Content that crosses the line includes:
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Sexual or Adult Content
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Sexually explicit material or nudity</li>
          <li>Content promoting adult services</li>
          <li>Sexual content involving minors (report immediately—this is illegal)</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Violent or Graphic Content
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Extreme violence, gore, or animal abuse</li>
          <li>Content promoting self-harm or suicide</li>
          <li>Dangerous stunts that could inspire harmful behavior</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Hateful or Abusive Content
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Content targeting individuals based on race, religion, caste, gender, disability, or sexual orientation</li>
          <li>Bullying, harassment, or threats</li>
          <li>Content promoting terrorism or extremism</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Spam and Misleading Content
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Scams, phishing, or fraudulent schemes</li>
          <li>Misleading thumbnails or titles (extreme clickbait)</li>
          <li>Videos designed solely to drive traffic to external sites</li>
          <li>Mass-uploaded duplicate content</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Copyright Violations
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Uploading movies, TV shows, or music videos without permission</li>
          <li>Re-uploading other creators content without authorization</li>
          <li>Using copyrighted music in the background without license</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          How to Report a Video
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Step-by-Step Reporting Process
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Locate the three-dot menu icon (⋮) on the video thumbnail or below the video player</li>
          <li>Click or tap the menu icon</li>
          <li>Select "Report" from the dropdown menu</li>
          <li>Choose the most accurate reason from the list:
            <Box component="ul" sx={{ mt: 1, ml: 3 }}>
              <li>Sexual content</li>
              <li>Violent or harmful content</li>
              <li>Hateful or abusive content</li>
              <li>Spam or misleading</li>
              <li>Copyright infringement</li>
              <li>Other (specify in text box)</li>
            </Box>
          </li>
          <li>Provide additional context in the text box if needed</li>
          <li>Click Submit</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          What Happens After You Report
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Review queue:</strong> Your report enters a moderation queue reviewed by IndeeClips team</li>
          <li><strong>Anonymous:</strong> The creator never knows who reported their video</li>
          <li><strong>Decision timeline:</strong> Most reports reviewed within 24-48 hours</li>
          <li><strong>Outcome:</strong> If content violates policies, it may be removed, age-restricted, or result in channel penalties</li>
          <li><strong>No notification:</strong> You typically won't be notified of the outcome (for privacy reasons)</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Reporting Channels (Repeat Offenders)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          If an entire channel consistently uploads inappropriate content:
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Visit the channel page</li>
          <li>Click the three-dot menu in the channel header</li>
          <li>Select "Report Channel"</li>
          <li>Choose reason and provide details about the pattern of violations</li>
          <li>Submit report</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Channel reports are taken seriously—repeated violations can result in permanent channel termination.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Reporting Comments
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Inappropriate comments can be reported separately:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Find the three-dot menu next to the comment</li>
          <li>Select "Report"</li>
          <li>Choose reason (hate speech, spam, harassment, etc.)</li>
          <li>Reported comments may be hidden or deleted if they violate policies</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          When NOT to Report
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Reporting should be reserved for policy violations, not personal preferences:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Don't report:</strong> Videos you simply don't like or disagree with</li>
          <li><strong>Don't report:</strong> Content from creators you're feuding with (personal disputes aren't violations)</li>
          <li><strong>Don't report:</strong> Opinions you find offensive but aren't hateful (free speech exists)</li>
          <li><strong>Don't report:</strong> Competitive channels to harm their growth (abuse of reporting system can get YOU penalized)</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Use "Not Interested" or "Don't Recommend Channel" for content you dislike but doesn't violate policies.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Alternative Actions Beyond Reporting
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Block a Channel
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          If you never want to see content from a specific creator, block them. This removes their videos from your recommendations without filing an official report.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Hide Comments
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          If comment sections get toxic, you can hide all comments on videos you're watching. This doesn't report anyone—just improves your viewing experience.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Reporting Safety Tips
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Be specific:</strong> In the text box, explain exactly what violates the policy—timestamps help</li>
          <li><strong>Don't engage:</strong> Don't comment or interact with violating content before reporting—just report and move on</li>
          <li><strong>One report is enough:</strong> Filing multiple reports for the same video doesn't speed up review</li>
          <li><strong>Urgent issues:</strong> For illegal content (child abuse, terrorism), contact local authorities in addition to reporting</li>
          <li><strong>Follow up:</strong> If extremely harmful content isn't removed after 48 hours, email contact@indeeclips.com directly</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Understanding Report Outcomes
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Not all reports result in removal. IndeeClips considers:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Context matters:</strong> Educational, documentary, or news content may show things otherwise prohibited if clearly labeled</li>
          <li><strong>Age-restriction:</strong> Some borderline content gets age-restricted rather than removed</li>
          <li><strong>Appeal process:</strong> Creators can appeal removals—sometimes content is reinstated after review</li>
          <li><strong>False reports:</strong> Users who repeatedly file false reports may lose reporting privileges</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Help Keep IndeeClips Safe
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
            Community reporting is our first line of defense against harmful content. When you report responsibly, you're protecting other viewers and maintaining the quality of the platform. If you see something that violates our policies, speak up. Your report matters.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Questions about reporting or concerns about specific content? Email{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ReportContent;
